import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import Navbar from "./Navbar";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const EditTest = () => {
    const { id, testId } = useParams(); // Extract category ID and test ID from URL
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategoryTests, setSelectedCategoryTests] = useState([]);
    const [payMode, setPayMode] = useState("Amount");
    const [globalAmount, setGlobalAmount] = useState("");
    const [globalTarget, setGlobalTarget] = useState("");

    // Fetch Tests for Selected Category
    useEffect(() => {
        if (!id) return;

        const token = localStorage.getItem("jwt_token");
        fetch(`${API_BASE_URL}api/getTestsByCatid?tc_id=${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
        })
            .then(response => response.json())
            .then(data => {
                if (data.STATUS && Array.isArray(data.data)) {
                    const testsWithFields = data.data.map(test => ({
                        ...test,
                        amount: test.amount || "",
                        target: test.target || "",
                        payType: test.payType || "Amount",
                    }));

                    setSelectedCategoryTests(testsWithFields);
                } else {
                    setError("No tests found for this category.");
                    setSelectedCategoryTests([]);
                }
            })
            .catch(error => {
                console.error("Error fetching tests:", error);
                setError("Failed to load tests.");
            })
            .finally(() => setLoading(false));
    }, [id]);

    // Handle Pay Mode change (Applies to all tests)
    const handlePayModeChange = (value) => {
        setPayMode(value);
        setGlobalAmount("");
        setGlobalTarget("");
        setSelectedCategoryTests(prevTests =>
            prevTests.map(test => ({
                ...test,
                amount: value === "Amount" ? "" : test.amount,
                target: value === "Target" ? "" : test.target
            }))
        );
    };

    // Apply global amount to all tests
    const handleGlobalAmountChange = (value) => {
        setGlobalAmount(value);
        setSelectedCategoryTests(prevTests =>
            prevTests.map(test => ({ ...test, amount: value }))
        );
    };

    // Apply global target to all tests
    const handleGlobalTargetChange = (value) => {
        setGlobalTarget(value);
        setSelectedCategoryTests(prevTests =>
            prevTests.map(test => ({ ...test, target: value }))
        );
    };

    // Handle individual test amount/target change
    const handleInputChange = (index, field, value) => {
        setSelectedCategoryTests(prevTests => {
            const updatedTests = [...prevTests];
            updatedTests[index][field] = value;
            return updatedTests;
        });
    };

    // Handle radio button change (Amount or Percentage)
    const handlePayTypeChange = (index, value) => {
        setSelectedCategoryTests(prevTests => {
            const updatedTests = [...prevTests];
            updatedTests[index].payType = value;
            return updatedTests;
        });
    };

    // Handle form submission
    const handleSubmit = () => {
        const formData = {
            categoryId: id, 
            testId: testId, 
            payMode: payMode,
            tests: selectedCategoryTests
        };

        console.log("Submitting form with data:", formData);

        fetch(`${API_BASE_URL}api/updateTests`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${localStorage.getItem("jwt_token")}`,
            },
            body: JSON.stringify(formData),
        })
        .then(response => response.json())
        .then(data => {
            if (data.STATUS) {
                alert("Tests updated successfully!");
            } else {
                alert("Failed to update tests.");
            }
        })
        .catch(error => console.error("Error updating tests:", error));
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p className="text-danger">{error}</p>;

    return (
        <div className="wrapper">
            <Sidebar />
            <main className="main-content content-page">
                <div className="position-relative">
                    <Navbar />
                </div>

                <div className="content-inner container-fluid pb-0">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between">
                                    <h4 className="card-title">Test Information</h4>
                                </div>
                                <div className="card-body">
                                    {/* Global Controls */}
                                    <div className="mb-3 d-flex gap-3">
                                        <div>
                                            <label className="form-label"><strong>Pay Mode:</strong></label>
                                            <select
                                                className="form-control"
                                                value={payMode}
                                                onChange={(e) => handlePayModeChange(e.target.value)}
                                            >
                                                <option value="Amount">Amount</option>
                                                <option value="Target">Target</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="form-label"><strong>Amount:</strong></label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="Enter amount"
                                                value={globalAmount}
                                                onChange={(e) => handleGlobalAmountChange(e.target.value)}
                                            />
                                        </div>

                                        {payMode === "Target" && (
                                            <div>
                                                <label className="form-label"><strong>Target:</strong></label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Enter target"
                                                    value={globalTarget}
                                                    onChange={(e) => handleGlobalTargetChange(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className="category-list-container" style={{ border: '1px solid #ccc', borderRadius: '10px', padding: '10px', height: '300px', overflowY: 'scroll' }}>
                                        <h5>Tests</h5>
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Test Name</th>
                                                    <th>Pay Type</th>
                                                    <th>Amount</th>
                                                    {payMode === "Target" && <th>Target</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedCategoryTests.length > 0 ? (
                                                    selectedCategoryTests.map((test, index) => (
                                                        <tr key={test.id}>
                                                            <td>{test.test_name}</td>
                                                            <td>
                                                                <div className="d-flex gap-2">
                                                                    <label>
                                                                        <input
                                                                            type="radio"
                                                                            name={`payType-${index}`}
                                                                            value="Amount"
                                                                            checked={test.payType === "Amount"}
                                                                            onChange={() => handlePayTypeChange(index, "Amount")}
                                                                        /> Amount
                                                                    </label>
                                                                    <label>
                                                                        <input
                                                                            type="radio"
                                                                            name={`payType-${index}`}
                                                                            value="Percentage"
                                                                            checked={test.payType === "Percentage"}
                                                                            onChange={() => handlePayTypeChange(index, "Percentage")}
                                                                        /> Percentage
                                                                    </label>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <input type="number" className="form-control" value={test.amount} onChange={(e) => handleInputChange(index, "amount", e.target.value)} />
                                                            </td>
                                                            {payMode === "Target" && (
                                                                <td>
                                                                    <input type="number" className="form-control" value={test.target} onChange={(e) => handleInputChange(index, "target", e.target.value)} />
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr><td colSpan={4} className="text-center">No Tests Available</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <button onClick={handleSubmit} className="btn btn-primary mt-3">Submit</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default EditTest;
